import React from 'react';
import {
  Card,
  CardBody,
  Row,
  CardSubtitle,
  CardTitle,
  Col,
} from 'reactstrap';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useBoolean } from 'usehooks-ts';

import { ReactComponent as NextIconSVG } from 'assets/images/location/next-icon.svg';
import { ReactComponent as PrevIconSVG } from 'assets/images/location/prev-icon.svg';

import Button from 'components/App/Button';
import AppSelectValidation from 'components/App/SelectValidation';
import FormField from 'components/AddLocation/FormField';
import SectionWrapper from 'components/AddLocation/SectionWrapper';
import ModalEditFacilityService from 'components/Modals/ModalEditFacilityService';

import INITIAL_VALUE from 'constants/initialValue';
import TAB from 'constants/tab';
import { LANGUAGE_LABEL, PARAM_SETTING } from 'constants/setting';
import { editLocationFormSteps } from 'utils/validate';

import styles from './StepAmenitiesService.module.scss';

function StepAmenitiesService(props) {
  const {
    setting,
    location,

    actionAddLocationByStep,
    onBack,
    setIsValid,
  } = props;

  const {
    value: isOpenModalService,
    setTrue: setOpenModalService,
    setFalse: setCloseModalService,
  } = useBoolean(false);

  const [isUpdateNeeded, setIsUpdateNeeded] = React.useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      mainLanguages: [],
      serviceList: setting[PARAM_SETTING.SERVICES],
      serviceOptionEdit: [],
    },

    validationSchema: Yup.object(editLocationFormSteps.stepAmenitiesService),

    onSubmit: (values) => {
      const listActivedServices = values.serviceList.map((item) => {
        const serviceChildrenActive = [];

        item.children.forEach((itemChildren) => {
          if (itemChildren.isActive) {
            serviceChildrenActive.push(itemChildren.name);
          }
        });

        if (serviceChildrenActive) {
          return {
            group: item.name,
            children: serviceChildrenActive,
          };
        }

        return {};
      });

      actionAddLocationByStep({
        step: TAB.ADD_IMAGES,
        stepAvailable: TAB.AMENITIES_SERVICES,
        location: {
          mainLanguages: values.mainLanguages.map((item) => item.value),
          services: listActivedServices,
        },
      });
    },
  }, [setting]);

  const onRenderActiveServiceOptions = React.useCallback((service) => {
    const activeList = [];

    service.forEach((item) => {
      if (item.isActive) {
        activeList.push(
          <div key={item.vi} className="ActiveService">
            {item.vi}
          </div>,
        );
      }
    });

    return (
      <div className="ActiveServiceList">
        {activeList.length > 0 ? activeList : '-'}
      </div>
    );
  }, []);

  // MODAL SERVICE OPTIONS
  const onOpenServiceOptions = React.useCallback((service) => {
    setOpenModalService();
    validation.setFieldValue('serviceOptionEdit', service, false);
  }, []);

  const onCancelModal = React.useCallback(() => {
    setCloseModalService();
    validation.setFieldValue('serviceOptionEdit', {}, false);
  }, []);

  const onChangeLanguage = React.useCallback((selected) => {
    validation.setFieldValue('mainLanguages', selected, true);

    actionAddLocationByStep({
      step: TAB.AMENITIES_SERVICES,
      location: {
        ...location,
        mainLanguages: selected.map((item) => item.value),
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  const onToggleCheckedService = React.useCallback((e) => {
    const { value } = e.target;

    validation.setFieldValue('serviceOptionEdit', {
      ...validation.values.serviceOptionEdit,
      children: validation.values.serviceOptionEdit.children.map((item) => {
        if (item.name === value) {
          return ({
            ...item,
            isActive: !item.isActive,
          });
        }

        return ({
          ...item,
        });
      }),
    }, false);
  }, [validation]);

  const onToggleAllService = React.useCallback((e) => {
    const { value } = e.target;

    validation.setFieldValue('serviceOptionEdit', {
      ...validation.values.serviceOptionEdit,
      children: validation.values.serviceOptionEdit.children.map((item) => ({
        ...item,
        isActive: +value === 1,
      })),
    }, false);
  }, [validation]);

  const listActivedServices = React.useMemo(() => validation.values.serviceList?.map((item) => {
    const serviceChildrenActive = [];

    item.children.forEach((itemChildren) => {
      if (itemChildren.isActive) {
        serviceChildrenActive.push(itemChildren.name);
      }
    });

    return {
      group: item.name,
      children: serviceChildrenActive,
    };
  }), [validation.values.serviceList]);

  const onConfirmModal = React.useCallback(() => {
    setCloseModalService();

    validation.setFieldValue('serviceList', validation.values.serviceList.map((service) => {
      if (service.name === validation.values.serviceOptionEdit.name) {
        return validation.values.serviceOptionEdit;
      }
      return service;
    }), false);
    validation.setFieldValue('serviceOptionEdit', {}, false);

    setIsUpdateNeeded(true);
  }, [setCloseModalService, validation]);

  React.useEffect(() => {
    if (isUpdateNeeded) {
      actionAddLocationByStep({
        step: TAB.AMENITIES_SERVICES,
        location: {
          ...location,
          services: listActivedServices,
        },
      });

      setIsUpdateNeeded(false);
    }
  }, [actionAddLocationByStep, isUpdateNeeded, location, listActivedServices]);

  const languageOptions = React.useMemo(() => {
    if (setting[PARAM_SETTING.LANGUAGES]) {
      const newLanguages = setting[PARAM_SETTING.LANGUAGES].map((item) => ({
        label: LANGUAGE_LABEL[item.name],
        value: item.name,
      }));

      return newLanguages;
    }

    return [];
  }, [setting]);

  React.useEffect(() => {
    setIsValid(validation.isValid);
  }, [setIsValid, validation.isValid]);

  return (
    <div className={styles.Component}>
      <form onSubmit={validation.handleSubmit}>
        <SectionWrapper
          isRequired
          title="Ngôn ngữ được sử dụng"
        >
          <FormField isHiddenLabel>
            <AppSelectValidation
              isMulti
              isHiddenClearable
              placeholder="Chọn ngôn ngữ"
              name="mainLanguages"
              key="mainLanguages"
              options={languageOptions}
              value={validation.values.mainLanguages}
              validation={validation}
              onChangeMulti={onChangeLanguage}
            />
          </FormField>
        </SectionWrapper>

        <Card className="Section">
          <CardBody className="Section__Information">
            {validation.values.serviceList?.map((service) => (
              <Col key={service.name}>
                <Row className="Section__Information__Field" key={service.name}>
                  <Col xs="8" lg="6">
                    <CardTitle className="Section__Information__Field__Title">
                      {service.vi}
                    </CardTitle>
                  </Col>

                  <Col xs="4" lg="6" className="d-flex justify-content-end align-items-center">
                    <Button
                      outline
                      classes="Section__Information__Field__Button__Edit"
                      value={service}
                      onClick={onOpenServiceOptions}
                    >
                      Chỉnh sửa
                    </Button>
                  </Col>
                </Row>

                <CardSubtitle className="Section__Information__Field__Subtitle">
                  {onRenderActiveServiceOptions(service.children)}
                </CardSubtitle>
              </Col>
            ))}
          </CardBody>
        </Card>

        <div className={styles.FormAction}>
          <Button
            classes="Btn Btn__PrevStep"
            outline
            onClick={onBack(TAB.ROOM_PRICE)}
          >
            <PrevIconSVG className="Icon__PrevStep" />
            Quay về bước trước
          </Button>

          <Button
            classes="Btn Btn__NextStep"
            type="submit"
          >
            Bước tiếp theo
            <NextIconSVG className="Icon__NextStep" />
          </Button>
        </div>
      </form>

      <ModalEditFacilityService
        isOpen={isOpenModalService}
        service={validation.values.serviceOptionEdit}
        onCancelModal={onCancelModal}
        onConfirmModal={onConfirmModal}
        onToggleCheckedService={onToggleCheckedService}
        onToggleAllService={onToggleAllService}
      />
    </div>
  );
}

StepAmenitiesService.propTypes = {
  setting: PropTypes.instanceOf(Object),
  location: PropTypes.instanceOf(Object).isRequired,
  actionAddLocationByStep: PropTypes.func,
  onBack: PropTypes.func,
  setIsValid: PropTypes.func,
};

StepAmenitiesService.defaultProps = {
  setting: INITIAL_VALUE.OBJECT,

  actionAddLocationByStep: INITIAL_VALUE.FUNCTION,
  onBack: INITIAL_VALUE.FUNCTION,
  setIsValid: INITIAL_VALUE.FUNCTION,
};

export default React.memo(StepAmenitiesService);
